import React from "react";

const DiagnosticPanel = ({ debugInfo, onToggle }) => {
  if (!debugInfo) return null;

  const hasData = Object.values(debugInfo.documentCounts || {}).some(
    (count) => count > 0
  );

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={onToggle}
        className={`px-4 py-2 rounded-full shadow-lg font-medium text-sm ${
          hasData
            ? "bg-green-500 hover:bg-green-600 text-white"
            : "bg-red-500 hover:bg-red-600 text-white animate-pulse"
        }`}
      >
        {hasData ? "✅ Data OK" : "⚠️ No Data - Debug"}
      </button>
    </div>
  );
};

export const DiagnosticModal = ({ debugInfo, isOpen, onClose }) => {
  if (!isOpen || !debugInfo) return null;

  const hasData = Object.values(debugInfo.documentCounts || {}).some(
    (count) => count > 0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">🔍 Analytics Diagnostics</h2>
              <p className="text-blue-100 text-sm mt-1">
                Troubleshooting data connection issues
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Overview */}
          <div
            className={`p-4 rounded-lg border-2 ${
              hasData
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500"
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{hasData ? "✅" : "❌"}</span>
              <div>
                <h3 className="font-bold text-lg">
                  {hasData
                    ? "Data Connection: OK"
                    : "Data Connection: No Data Found"}
                </h3>
                <p className="text-sm text-gray-600">
                  {hasData
                    ? "Dashboard is receiving analytics data from Firebase"
                    : "No analytics data found. See troubleshooting steps below."}
                </p>
              </div>
            </div>
          </div>

          {/* Environment Configuration */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>⚙️</span> Configuration
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Environment Mode:</span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    debugInfo.isDev
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {debugInfo.isDev ? "Development" : "Production"}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Dashboard is looking for{" "}
                <span className="font-mono bg-gray-200 px-2 py-1 rounded">
                  {debugInfo.isDev ? "*_dev" : "production"}
                </span>{" "}
                collections
              </div>
            </div>
          </div>

          {/* Collection Names */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>📁</span> Firestore Collections Being Queried
            </h3>
            <div className="space-y-2">
              {Object.entries(debugInfo.collectionsQueried || {}).map(
                ([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                  >
                    <span className="text-sm text-gray-600 font-medium">
                      {key}:
                    </span>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {value}
                    </code>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Document Counts */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span>📊</span> Data Found in Collections
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(debugInfo.documentCounts || {}).map(
                ([key, count]) => (
                  <div
                    key={key}
                    className={`p-3 rounded-lg border-2 ${
                      count > 0
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="text-xs text-gray-600 mb-1">{key}</div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-2xl font-bold ${
                          count > 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {count}
                      </span>
                      <span className="text-xs text-gray-500">docs</span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Troubleshooting Steps */}
          {!hasData && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>💡</span> Troubleshooting Steps
              </h3>
              <ol className="list-decimal list-inside space-y-3 text-sm">
                <li className="text-gray-700">
                  <span className="font-medium">
                    Check Flutter App Environment:
                  </span>
                  <div className="ml-6 mt-1 text-gray-600">
                    In your Flutter app, verify{" "}
                    <code className="bg-gray-200 px-1 rounded">
                      lib/constants/config.dart
                    </code>
                    <br />
                    Ensure{" "}
                    <code className="bg-gray-200 px-1 rounded">isDev</code>{" "}
                    matches dashboard setting:{" "}
                    <span className="font-mono font-bold">
                      {debugInfo.isDev ? "true" : "false"}
                    </span>
                  </div>
                </li>

                <li className="text-gray-700">
                  <span className="font-medium">Generate Test Data:</span>
                  <div className="ml-6 mt-1 text-gray-600">
                    Open your Flutter app and:
                    <ul className="list-disc list-inside mt-1 ml-2">
                      <li>Login/logout to create session events</li>
                      <li>Browse clubs to generate analytics</li>
                      <li>Create a booking to test booking events</li>
                      <li>Wait 1-2 minutes for data to sync</li>
                    </ul>
                  </div>
                </li>

                <li className="text-gray-700">
                  <span className="font-medium">Check Firestore Console:</span>
                  <div className="ml-6 mt-1 text-gray-600">
                    Open{" "}
                    <a
                      href="https://console.firebase.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Firebase Console
                    </a>
                    <br />
                    Go to Firestore Database and verify these collections exist:
                    <ul className="list-disc list-inside mt-1 ml-2">
                      {Object.values(debugInfo.collectionsQueried || {}).map(
                        (collectionName) => (
                          <li key={collectionName}>
                            <code className="bg-gray-200 px-1 rounded">
                              {collectionName}
                            </code>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </li>

                <li className="text-gray-700">
                  <span className="font-medium">
                    Try Switching Environment Mode:
                  </span>
                  <div className="ml-6 mt-1 text-gray-600">
                    In{" "}
                    <code className="bg-gray-200 px-1 rounded">
                      src/services/firebaseAnalyticsService.js
                    </code>
                    <br />
                    Change{" "}
                    <code className="bg-gray-200 px-1 rounded">
                      this.isDev = {debugInfo.isDev ? "false" : "true"}
                    </code>{" "}
                    and refresh
                  </div>
                </li>

                <li className="text-gray-700">
                  <span className="font-medium">Check Browser Console:</span>
                  <div className="ml-6 mt-1 text-gray-600">
                    Press F12 to open Developer Tools and look for detailed logs
                    about which collections are being queried and any errors.
                  </div>
                </li>
              </ol>
            </div>
          )}

          {/* Success Message */}
          {hasData && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span>✅</span> Everything looks good!
              </h3>
              <p className="text-sm text-gray-700">
                Your dashboard is successfully connected to Firebase and
                receiving analytics data. All metrics should be displaying
                correctly.
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-lg border-t">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Close Diagnostics
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPanel;
